import { memo, useCallback, useMemo, useState } from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import SettingsIcon from "@mui/icons-material/Settings";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { useQuestionnaireManagement } from "src-next/modules/questionnaire-builder/questionnaire-management/hooks";
import { oids } from "@/configs/Settings";

import OrgLaneCard from "src-next/shared/ui/cards/OrgLaneCard";
import SectionHeader from "src-next/shared/ui/sections/SectionHeader";
import { WHATSAPP_CHANNEL_AUTOMATIONS_IMPACT_COPY } from "../../management/constants/whatsappChannelAutomationsCopy";

import {
  isSystemDefaultAutomation,
  type SystemDefaultAutomationId,
} from "../constants/automationUiConstants";
import { toAutomationPanelItem } from "../transformers/automationTransformers";
import { resolveQuestionnaireIdFromTemplate } from "../helpers/automationTemplateUtils";
import type { AutomationId, AutomationItem } from "../types";
import AutomationConfiguredTemplateSummary from "./AutomationConfiguredTemplateSummary";
import AutomationMessageTemplatePreviewDialog from "./AutomationMessageTemplatePreviewDialog";
import type { MessageTemplate } from "../../messageTemplates/types";

export interface AutomationsPanelProps {
  automations: AutomationItem[];
  organizationId: string;
  onActivateAutomation: (automationId: AutomationId) => void;
  onEditAutomation: (automationId: AutomationId) => void;
  onDisableAutomation: (automationId: AutomationId) => void;
  onNavigateToDefaultTemplates?: (
    automationId: SystemDefaultAutomationId,
  ) => void;
  canEditDefaultTemplates?: boolean;
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
  dragHandleProps?: {
    attributes?: object;
    listeners?: object;
  };
  hasConfiguredCustomAutomations?: boolean;
}

const AutomationsPanel = memo<AutomationsPanelProps>(
  ({
    automations,
    organizationId,
    onActivateAutomation,
    onEditAutomation,
    onDisableAutomation,
    onNavigateToDefaultTemplates,
    canEditDefaultTemplates = false,
    isCollapsed = false,
    onToggleCollapsed,
    dragHandleProps,
    hasConfiguredCustomAutomations = false,
  }) => {
    const [templateForPreview, setTemplateForPreview] =
      useState<MessageTemplate | null>(null);

    const { questionnaires } = useQuestionnaireManagement({
      organizationId: `${oids.cnpj}-${organizationId}`,
    });

    const questionnaireById = useMemo(
      () =>
        new Map(
          questionnaires
            .filter((item) => Boolean(item.id))
            .map((item) => [item.id as string, item]),
        ),
      [questionnaires],
    );

    const handleActivate = useCallback(
      (automationId: AutomationId) => {
        onActivateAutomation(automationId);
      },
      [onActivateAutomation],
    );

    const handleEdit = useCallback(
      (automationId: AutomationId) => {
        onEditAutomation(automationId);
      },
      [onEditAutomation],
    );

    const handleDisable = useCallback(
      (automationId: AutomationId) => {
        onDisableAutomation(automationId);
      },
      [onDisableAutomation],
    );

    const handleNavigateToDefaultTemplates = useCallback(
      (automationId: SystemDefaultAutomationId) => {
        onNavigateToDefaultTemplates?.(automationId);
      },
      [onNavigateToDefaultTemplates],
    );

    const handleViewTemplate = useCallback((template: MessageTemplate) => {
      setTemplateForPreview(template);
    }, []);

    const handleCloseTemplatePreview = useCallback(() => {
      setTemplateForPreview(null);
    }, []);

    const templatePreviewQuestionnaire = useMemo(() => {
      if (!templateForPreview) return null;
      const questionnaireId = resolveQuestionnaireIdFromTemplate(templateForPreview);
      if (!questionnaireId) return null;
      return questionnaireById.get(questionnaireId) ?? null;
    }, [questionnaireById, templateForPreview]);

    const cards = useMemo(() => {
      return automations.map((automation) => {
        const panelItem = toAutomationPanelItem(automation);
        const isSystemDefault = isSystemDefaultAutomation(panelItem.id);
        const showEditButton = !isSystemDefault && panelItem.isConfigured;
        const cardColor = isSystemDefault || panelItem.isConfigured
          ? "success"
          : panelItem.color;

        const handleSwitchChange = (
          _event: React.ChangeEvent<HTMLInputElement>,
          isChecked: boolean,
        ) => {
          if (isSystemDefault) return;

          if (isChecked) {
            handleActivate(panelItem.id);
            return;
          }

          handleDisable(panelItem.id);
        };

        return (
          <OrgLaneCard
            key={panelItem.id}
            icon={<SettingsIcon />}
            title={panelItem.title}
            color={cardColor}
            headerRight={
              <Stack direction="row" alignItems="center" spacing={1}>
                {showEditButton && (
                  <Button
                    variant="text"
                    onClick={() => handleEdit(panelItem.id)}
                    sx={{ textTransform: "none", minWidth: 0, px: 0.75 }}
                  >
                    Editar
                  </Button>
                )}

                <Switch
                  checked={isSystemDefault ? true : automation.isEnabled}
                  disabled={isSystemDefault}
                  onChange={handleSwitchChange}
                  inputProps={{
                    "aria-label": isSystemDefault
                      ? `Automação padrão do sistema ${panelItem.title}`
                      : `Habilitar automação ${panelItem.title}`,
                  }}
                />
              </Stack>
            }
            sx={{ height: "100%" }}
          >
            <Stack spacing={1}>
              {isSystemDefault && (
                <Chip
                  label="Padrão do sistema"
                  size="small"
                  color="default"
                  variant="outlined"
                  sx={{ alignSelf: "flex-start" }}
                />
              )}

              <Typography variant="body2" color="text.secondary">
                {panelItem.description}
              </Typography>

              {panelItem.triggerMoment ? (
                <Typography variant="caption" color="text.secondary">
                  <strong>Momento do disparo:</strong> {panelItem.triggerMoment}
                </Typography>
              ) : null}

              {isSystemDefault && (
                <Stack spacing={0.75}>
                  <Typography variant="body2" color="text.secondary">
                    {canEditDefaultTemplates
                      ? "Visualize e personalize o conteúdo da mensagem na seção Modelos Padrão."
                      : "Visualize o conteúdo da mensagem na seção Modelos Padrão. Para personalizar, conecte sua conta Meta."}
                  </Typography>

                  <Button
                    variant="text"
                    size="small"
                    endIcon={<ArrowForwardIcon fontSize="small" />}
                    onClick={() => {
                      if (!isSystemDefaultAutomation(panelItem.id)) return;
                      handleNavigateToDefaultTemplates(panelItem.id);
                    }}
                    sx={{
                      alignSelf: "flex-start",
                      textTransform: "none",
                      px: 0,
                      minWidth: 0,
                    }}
                  >
                    Ir para modelos padrão
                  </Button>
                </Stack>
              )}

              {!isSystemDefault && panelItem.isConfigured && panelItem.linkedMessageTemplate ? (
                <AutomationConfiguredTemplateSummary
                  template={panelItem.linkedMessageTemplate}
                  organizationId={organizationId}
                  onViewTemplate={() => {
                    handleViewTemplate(panelItem.linkedMessageTemplate as MessageTemplate);
                  }}
                />
              ) : null}

              {!isSystemDefault && panelItem.isConfigured && !panelItem.linkedMessageTemplate ? (
                <Typography variant="body2" color="text.secondary">
                  Modelo configurado não encontrado ou indisponível.
                </Typography>
              ) : null}
            </Stack>
          </OrgLaneCard>
        );
      });
    }, [
      automations,
      canEditDefaultTemplates,
      organizationId,
      handleActivate,
      handleDisable,
      handleEdit,
      handleNavigateToDefaultTemplates,
      handleViewTemplate,
    ]);

    return (
      <Box>
        <SectionHeader
          icon={<SettingsIcon fontSize="small" />}
          title="Painel de automações"
          subtitle={`Confirmação, cancelamento e pagamento são padrões do sistema. Personalize as mensagens em Modelos Padrão. ${WHATSAPP_CHANNEL_AUTOMATIONS_IMPACT_COPY.automationsPanelSubtitle}`}
          isCollapsed={isCollapsed}
          onCollapsedChange={
            onToggleCollapsed ? () => onToggleCollapsed() : undefined
          }
          actions={
            <IconButton
              aria-label="Reordenar seção"
              sx={{ cursor: "grab" }}
              {...(dragHandleProps?.attributes ?? {})}
              {...(dragHandleProps?.listeners ?? {})}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          }
        >
          {hasConfiguredCustomAutomations ? (
            <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
              {WHATSAPP_CHANNEL_AUTOMATIONS_IMPACT_COPY.alert}
            </Alert>
          ) : null}
          <Box
            sx={{
              mt: 1.5,
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
            }}
          >
            {cards}
          </Box>
        </SectionHeader>

        <AutomationMessageTemplatePreviewDialog
          open={Boolean(templateForPreview)}
          template={templateForPreview}
          organizationId={organizationId}
          questionnaire={templatePreviewQuestionnaire}
          onClose={handleCloseTemplatePreview}
        />
      </Box>
    );
  },
);

AutomationsPanel.displayName = "AutomationsPanel";

export default AutomationsPanel;
